import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider } from "react-router-dom";
import { AuthProvider, useAuth } from "./auth/AuthContext";
import { createAppRouter } from "./router";
import { applyDocumentLang, useTranslation } from "./i18n";

// Self-hosted fonts. An airgapped deployment cannot reach fonts.googleapis.com,
// so the files are bundled and the CSP allows no external font origin.
// Latin subset only: the UI ships de and en.
import "@fontsource/archivo/latin-500.css";
import "@fontsource/archivo/latin-600.css";
import "@fontsource/archivo/latin-700.css";
import "@fontsource/ibm-plex-sans/latin-400.css";
import "@fontsource/ibm-plex-sans/latin-500.css";
import "@fontsource/ibm-plex-sans/latin-600.css";
import "@fontsource/ibm-plex-mono/latin-400.css";
import "@fontsource/ibm-plex-mono/latin-500.css";
import "@fontsource/ibm-plex-mono/latin-600.css";

import "./index.css";

// Set dark theme as default
document.documentElement.dataset.theme = "light";

// Stamp the stored locale on <html lang> before the first paint, so a screen
// reader picks the right voice and BITV 2.0's language declaration holds.
applyDocumentLang();

const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error("Root element #root not found in document");
}

/**
 * Inner component that reads auth state and creates the router.
 */
function AuthenticatedApp(): React.JSX.Element {
  const { isLoading, user, logout } = useAuth();
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <output
        aria-label={t("common.loading")}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          background: "var(--bg)",
          color: "var(--t3)",
          font: "500 13px var(--fb)",
        }}
      >
        {t("common.loading")}
      </output>
    );
  }

  // Default to platform-admin for demo when not authenticated
  const shellUser = {
    name: user?.username ?? "Demo User",
    role: (user?.role === "tenant-admin" ? "tenant-admin" : "platform-admin") as
      | "platform-admin"
      | "tenant-admin",
    onLogout: logout,
  };

  const router = createAppRouter(shellUser);

  return <RouterProvider router={router} />;
}

createRoot(rootElement).render(
  <StrictMode>
    <AuthProvider>
      <AuthenticatedApp />
    </AuthProvider>
  </StrictMode>,
);
